import { useRef, useState, type FormEvent, type ReactNode } from 'react'
import type { ManufacturingRecord } from '@repo/shared'

export interface WorkflowField {
  name: string
  label: string
  type?: 'text' | 'number' | 'date' | 'textarea' | 'checkbox'
  optional?: boolean
  options?: Array<{ value: string; label: string }>
  value?: string
}
export function WorkflowForm({
  title,
  fields,
  onSubmit,
  disabled = false,
  submitLabel,
  queuedLabel,
  errorLabel,
}: {
  title: string
  fields: WorkflowField[]
  onSubmit: (fields: Record<string, string>) => Promise<unknown>
  disabled?: boolean
  submitLabel: string
  queuedLabel: string
  errorLabel: string
}) {
  const [busy, setBusy] = useState(false),
    [notice, setNotice] = useState<'queued' | 'error' | null>(null)
  const submitting = useRef(false)
  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (disabled || submitting.current) return
    submitting.current = true
    setBusy(true)
    setNotice(null)
    try {
      const values = Object.fromEntries(
        new FormData(e.currentTarget as HTMLFormElement).entries(),
      ) as Record<string, string>
      await onSubmit(values)
      setNotice('queued')
    } catch {
      setNotice('error')
    } finally {
      submitting.current = false
      setBusy(false)
    }
  }
  return (
    <form
      onSubmit={(e) => void submit(e)}
      aria-label={title}
      aria-busy={busy}
      className="space-y-3 rounded-lg border p-4"
    >
      <h3 className="font-semibold">{title}</h3>
      <fieldset disabled={disabled || busy} className="grid gap-3 sm:grid-cols-2">
        {fields.map((f) => (
          <label key={f.name} className="flex flex-col gap-1 text-sm">
            {f.label}
            {f.options ? (
              <select
                name={f.name}
                required={!f.optional}
                defaultValue={f.value ?? ''}
                className="rounded border bg-white p-2 text-gray-950"
              >
                <option value="">—</option>
                {f.options.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            ) : f.type === 'textarea' ? (
              <textarea
                name={f.name}
                maxLength={4000}
                required={!f.optional}
                defaultValue={f.value}
                className="rounded border bg-white p-2 text-gray-950"
              />
            ) : (
              <input
                name={f.name}
                type={f.type ?? 'text'}
                required={!f.optional}
                defaultValue={f.value}
                step={f.type === 'number' ? 'any' : undefined}
                min={f.type === 'number' ? '0' : undefined}
                maxLength={4000}
                className="rounded border bg-white p-2 text-gray-950"
              />
            )}
          </label>
        ))}
        <button
          type="submit"
          className="rounded bg-green-800 px-4 py-2 font-semibold text-white disabled:opacity-40"
        >
          {submitLabel}
        </button>
      </fieldset>
      {notice ? (
        <p role={notice === 'error' ? 'alert' : 'status'}>
          {notice === 'queued' ? queuedLabel : errorLabel}
        </p>
      ) : null}
    </form>
  )
}

export function WorkflowTable({
  title,
  items,
  columns,
}: {
  title: string
  items: ManufacturingRecord[]
  columns: Array<{ label: string; value: (record: ManufacturingRecord) => ReactNode }>
}) {
  return (
    <section className="space-y-2">
      <h2 className="text-lg font-semibold">
        {title} ({items.length})
      </h2>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-left text-sm">
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c.label} scope="col" className="whitespace-nowrap border-b p-2">
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id}>
                {columns.map((c) => (
                  <td key={c.label} className="border-b p-2 align-top">
                    {c.value(item)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}
